import React, { useRef, useState, useEffect } from 'react';
import ProductCard from '../../ProductCard.js';

const ProductGrid = ({
  loading,
  error,
  products,
  selectedProduct,
  selectedMainCategory,
  selectedSubCategory,
  isProductInBuild,
  onProductSelect,
  onProductToggle,
  onProductInfo,
  productInBuild,
  categoryConfig,
  hasRealSubCategories
}) => {
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkOverflow = () => {
    if (!scrollRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    const hasOverflow = scrollWidth > clientWidth;
    
    setShowLeftArrow(hasOverflow && scrollLeft > 10);
    setShowRightArrow(hasOverflow && scrollLeft < scrollWidth - clientWidth - 10);
  };

  const handleScroll = (direction) => {
    if (!scrollRef.current) return;
    
    const scrollAmount = 350;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    checkOverflow();
    const timer = setTimeout(checkOverflow, 100);
    return () => clearTimeout(timer);
  }, [products]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    container.addEventListener('scroll', checkOverflow);
    window.addEventListener('resize', checkOverflow);

    return () => {
      container.removeEventListener('scroll', checkOverflow);
      window.removeEventListener('resize', checkOverflow);
    };
  }, []);

  if (loading) {
    return (
      <div className="h-[260px] flex items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <div className="w-8 h-8 border-2 border-gray-400 border-t-blue-600 rounded-full animate-spin"></div>
          <div className="text-gray-600 text-sm">Loading products...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-[260px] flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-sm font-medium mb-1">⚠️ Connection Error</div>
          <div className="text-gray-600 text-xs">Using demo data</div>
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="h-[260px] flex items-center justify-center">
        <div className="text-gray-600 text-center">
          <div className="text-3xl mb-3">🏁</div>
          <div className="font-medium mb-1 text-sm">
            {!selectedMainCategory
              ? 'Select a category to see products'
              : !selectedSubCategory && hasRealSubCategories(selectedMainCategory)
              ? 'Select a subcategory to see products'
              : 'No products found'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[260px] relative">
      
      {showLeftArrow && (
        <button
          onClick={() => handleScroll('left')}
          className="absolute left-2 top-1/2 -translate-y-1/2 z-30 w-10 h-10 bg-white rounded-full shadow-lg hover:shadow-xl flex items-center justify-center transition-all hover:scale-110"
          aria-label="Scroll left"
        >
          <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {showRightArrow && (
        <button
          onClick={() => handleScroll('right')}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-30 w-10 h-10 bg-white rounded-full shadow-lg hover:shadow-xl flex items-center justify-center transition-all hover:scale-110"
          aria-label="Scroll right"
        >
          <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      <div
        ref={scrollRef}
        className="h-full overflow-x-auto scrollbar-hide"
        style={{ 
          display: 'flex',
          alignItems: 'center',
          scrollSnapType: 'x mandatory',
          // Tighter inset, still safe from arrow overlap
          paddingLeft: showLeftArrow ? '56px' : '24px',
          paddingRight: showRightArrow ? '56px' : '24px',
          // Keep snap starting offset consistent with padding
          scrollPaddingLeft: showLeftArrow ? '56px' : '24px',
          gap: '10px'
        }}
      >
        {products.map((product, index) => (
          <ProductCard
            key={product.id}
            product={product}
            index={index}
            isSelected={index === selectedProduct}
            isInBuild={isProductInBuild(product)}
            compatibilityStatus={product._compatibilityStatus}
            onSelect={onProductSelect}
            onToggle={onProductToggle}
            onInfo={onProductInfo}
            mainCategory={selectedMainCategory}
            subCategory={selectedSubCategory}
            productInBuild={productInBuild}
            categoryConfig={categoryConfig}
          />
        ))}
      </div>
    </div>
  );
};

export default ProductGrid;
